import { useState, useMemo } from "react";
import { Buildings, GridFour, Hash, Cube, CaretRight, CaretDown } from "@phosphor-icons/react";

const TYPE_META = {
  LOBBY: { icon: Buildings, color: "text-orange-600", label: "Lobby" },
  SECTOR: { icon: GridFour, color: "text-blue-600", label: "Sector" },
  BLOCK: { icon: Cube, color: "text-emerald-600", label: "Block" },
  BUILDING: { icon: Buildings, color: "text-purple-600", label: "Building" },
  FLAT: { icon: Hash, color: "text-slate-600", label: "Flat" },
};

// flatten tree into rows respecting open/closed state
function flatten(roots, openSet) {
  const out = [];
  const stack = [];
  for (let i = roots.length - 1; i >= 0; i--) stack.push({ node: roots[i], level: 0 });
  while (stack.length) {
    const { node, level } = stack.pop();
    const hasChildren = node.children && node.children.length > 0;
    const isOpen = openSet.has(node.id);
    out.push({ node, level, hasChildren, isOpen });
    if (hasChildren && isOpen) {
      for (let i = node.children.length - 1; i >= 0; i--) {
        stack.push({ node: node.children[i], level: level + 1 });
      }
    }
  }
  return out;
}

function collectInitialOpen(roots, maxLevel = 1) {
  const set = new Set();
  const stack = roots.map((r) => ({ node: r, level: 0 }));
  while (stack.length) {
    const { node, level } = stack.pop();
    if (level <= maxLevel && node.children && node.children.length) set.add(node.id);
    if (node.children) node.children.forEach((c) => stack.push({ node: c, level: level + 1 }));
  }
  return set;
}

export default function TreeView({ roots = [] }) {
  const [openSet, setOpenSet] = useState(() => collectInitialOpen(roots, 1));
  // re-init when roots change
  useMemo(() => {
    setOpenSet(collectInitialOpen(roots, 1));
  }, [roots]);

  const rows = useMemo(() => flatten(roots, openSet), [roots, openSet]);

  const toggle = (id) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div data-testid="tree-view">
      {rows.map(({ node, level, hasChildren, isOpen }) => {
        const meta = TYPE_META[node.type] || {};
        const Icon = meta.icon || Buildings;
        return (
          <div
            key={node.id}
            className="flex items-center gap-2 py-2 px-3 hover:bg-slate-50 border-b border-slate-100 cursor-pointer transition-all"
            style={{ paddingLeft: 12 + level * 24 }}
            onClick={() => hasChildren && toggle(node.id)}
            data-testid={`tree-node-${node.id}`}
          >
            <span className="w-4 text-slate-400">
              {hasChildren ? (isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />) : ""}
            </span>
            <Icon size={16} className={meta.color} weight="regular" />
            <span className="mono-label">{meta.label}</span>
            <span className="text-sm text-slate-900 font-medium flex-1">{node.name}</span>
            {node.entry_count > 0 && (
              <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 text-slate-700 border border-slate-200">
                {node.entry_count} doc{node.entry_count !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
